<template>
  <main class="merkliste">
    <Breadcrumb
      :items="[{ label: 'Merkliste', to: $route.path }]"
      with-brodos
    />
    <div class="grid">
      <div class="grid__item grid__item--12">
        <h1 class="merkliste__headline text--center">Deine Wunschartikel auf einen Blick</h1>
        <div class="merkliste__results-count text--center">
          <span v-if="isLoading">Lade Artikel...</span>
          <span v-else>{{ productBookmarks.length }} Artikel</span>
        </div>
      </div>
    </div>
    <ClientOnly>
      <div
        v-if="loadedOffers && productBookmarks.length > 0"
        class="merkliste__filters"
      >
        <FilterAccordion
          v-if="loadedOffers.aggregations"
          :aggregations="loadedOffers.aggregations"
          :current-offers-count="currentOffers.length"
          :variables="listViewParams"
          :hidden-filters="['providers', 'productCategories', 'wlan']"
          hide-sort-dropdown
          @submit="updateFilter"
        >
          <template #additionalFilters>
            <div class="merkliste__filter-toggle">
              <Toggle
                :value="showOnlyPriceReductions"
                class="merkliste__price-reduction-toggle"
                @input="toggleShowOnlyPriceReductions"
              >
                Preissenkung
              </Toggle>
            </div>
          </template>
        </FilterAccordion>
      </div>
      <GridContainer v-if="currentOffers">
        <OfferTilesContainer
          :offers="currentOffers"
          has-cart-button
        />
      </GridContainer>
    </ClientOnly>
  </main>
</template>

<script>
import { ref } from 'vue';
import Breadcrumb from '@/components/breadcrumb.vue';

import PageHeaderFromContentData from '@/mixins/page-header-from-content-data';
import PageTracking from '@/mixins/page-tracking';
import useProductBookmarks from '@/composables/product-bookmarks';
import { GridContainer, Toggle } from '@i22-td-smarthome/component-library';
import FilterAccordion from '~/components/filter/filter-accordion.vue';
import { getPrice } from '@i22/rocket';
import OfferTilesContainer from '~/components/offer-tiles-container.vue';
import { getHeader } from '~/helpers/page-header-compositon';
import { Track } from '~/helpers/page-tracking';

export default {
  components: {
    OfferTilesContainer,
    FilterAccordion,
    GridContainer,
    Toggle,
    Breadcrumb,
  },
  mixins: [PageHeaderFromContentData, PageTracking()],
  watch: {
    productBookmarks: {
      handler(value) {
        if (process.server) return;
        if (this.productBookmarksValue === JSON.stringify(value)) return;
        this.productBookmarksValue = JSON.stringify(value);
        this.refreshOffers();
      },
      immediate: true,
    },
    filterVariables: {
      handler() {
        if (process.server) return;
        this.refreshOffers();
      },
    },
  },
  data() {
    return {
      productBookmarksValue: undefined,
      loadedOffers: null,
      isLoading: true,
      showOnlyPriceReductions: false,
      listViewParams: {
        grouped: true,
        filter: {
          listed: true,
          types: ['TANGIBLE', 'MIXED'],
          productAvailable: true,
          hasAlwaysDisplayed: true,
        },
      },
      filterVariables: undefined,
    };
  },
  setup() {
    Track({ page_type: 'theme.landing' });

    getHeader({
      title: ref('Merkliste'),
      isIndexed: ref(false),
      description: ref('Ihre Merkliste mit allen ausgewählten Produkten'),
    });

    const { productBookmarks, fetchBookmarkedOffers } = useProductBookmarks();
    return { productBookmarks, fetchBookmarkedOffers };
  },
  computed: {
    isFiltered() {
      const filter = this.filterVariables?.filter;
      if (!filter) return false;
      return (
        filter.brandSlugs?.length > 0 ||
        filter.colorSlugs?.length > 0 ||
        filter?.compatibleProductFamilySlugs?.length > 0 ||
        !!filter?.onetimePrice
      );
    },
    currentOffers() {
      return this.productBookmarks
        .map((bookmark) => {
          const offer = this.loadedOffers?.entries?.find(
            (o) => o?.product?.articleNumber === bookmark.articleNumber
          );
          if (offer) {
            // do not show offer, when the price has not changed.
            if (
              this.showOnlyPriceReductions &&
              bookmark.price <= getPrice(offer.prices, 'DEFAULT', 'ONETIME')?.value
            ) {
              return undefined;
            }
            return offer;
          }

          // do not show empty entry, when filter is active
          if (this.isFiltered || this.showOnlyPriceReductions) return undefined;

          return {
            product: {
              articleNumber: bookmark.articleNumber,
              family: {
                name: bookmark.name,
              },
            },
          };
        })
        .filter(Boolean);
    },
  },
  methods: {
    toggleShowOnlyPriceReductions() {
      this.showOnlyPriceReductions = !this.showOnlyPriceReductions;
    },
    updateFilter(newVariables) {
      if (JSON.stringify(newVariables) === JSON.stringify(this.filterVariables)) return;
      this.filterVariables = newVariables;
    },

    async refreshOffers() {
      this.loadedOffers = await this.fetchBookmarkedOffers(this.filterVariables);
      this.isLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.merkliste {
  &__headline {
    position: relative;
    margin: {
      top: $grid-medium-gutter;
      bottom: $grid-small-gutter;
    }
  }

  &__filter-toggle {
    grid-area: sort;
    align-self: flex-end;
  }

  &__price-reduction-toggle {
    padding-inline: $grid-base;
  }

  &__results-count {
    font-size: 1.75rem;
    margin-bottom: $grid-medium-gutter;
    position: relative;
  }
}
</style>
